import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { ArrowRightIcon, CaretLeftIcon } from "@phosphor-icons/react";
import { CATEGORY_IMAGES, CATEGORY_FALLBACK_IMAGE } from "@/lib/categoryImages";
import CategorySubscribeButton from "@/components/CategorySubscribeButton";

/**
 * Categories — full marketplace lane directory.
 *
 * Layout split: on **mobile** this is a focused, full-bleed grid of
 * category tiles only — no Navbar, no Footer, no marketing copy, no
 * subscribe button (those create scroll fatigue on small screens).
 * A single minimal back-button overlay handles navigation. On
 * **desktop** the full chrome (Navbar, heading, Footer, subscribe
 * controls) renders normally.
 */
export default function Categories() {
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories));
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="categories-page">
      {/* Desktop chrome only — hidden on mobile to keep the page laser-focused
          on category discovery. The MobileTabBar (mounted at App root)
          handles primary navigation on small screens. */}
      <div className="hidden md:block">
        <Navbar />
      </div>

      {/* Mobile back button — single floating chevron in the top-left so
          users can return to the previous screen without a full Navbar. */}
      <Link
        to="/"
        aria-label="Back"
        className="md:hidden fixed top-3 left-3 z-30 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/90 backdrop-blur border border-zinc-200 shadow-sm active:scale-95"
        data-testid="categories-mobile-back"
      >
        <CaretLeftIcon size={20} weight="bold" />
      </Link>

      <main className="flex-1 w-full max-w-7xl mx-auto px-3 sm:px-6 pt-16 sm:pt-10 pb-10">
        {/* Heading hidden on mobile per spec — the grid stands alone. */}
        <h1 className="hidden md:block font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
          Covering every lane
        </h1>
        <div
          className="mt-0 md:mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4"
          data-testid="categories-grid"
        >
          {categories.map((c) => (
            <div
              key={c}
              className="group block border border-zinc-200 bg-white overflow-hidden hover-lift rounded-xl relative"
              data-testid={`cat-${c}`}
            >
              <Link to={`/browse?category=${encodeURIComponent(c)}`}>
                <div className="aspect-[3/2] bg-zinc-100 relative overflow-hidden">
                  <img
                    src={CATEGORY_IMAGES[c] || CATEGORY_FALLBACK_IMAGE}
                    alt={c}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      if (e.currentTarget.src.indexOf(CATEGORY_FALLBACK_IMAGE) === -1) {
                        e.currentTarget.src = CATEGORY_FALLBACK_IMAGE;
                      }
                    }}
                  />
                </div>
                <div className="p-2 sm:p-4 flex items-center justify-between gap-2">
                  <p className="font-display font-semibold text-xs sm:text-base leading-tight">
                    {c}
                  </p>
                  <ArrowRightIcon size={14} className="text-zinc-400 group-hover:text-[#1D78AB] flex-shrink-0" />
                </div>
              </Link>
              {/* Subscribe control is desktop-only — on mobile the
                  bare category card keeps the screen calm. */}
              <div className="hidden md:block px-2.5 pb-3 sm:px-4 sm:pb-4">
                <CategorySubscribeButton category={c} />
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Footer hidden on mobile — MobileTabBar covers navigation. */}
      <div className="hidden md:block">
        <Footer />
      </div>
    </div>
  );
}
